import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import {
  useBandProfileView,
  bandInitials,
  type FriendProfileFriend,
} from "../../app/hooks/useFriends.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { MembersSection, ShowsSection } from "./BandProfileModal.js";
import styles from "./MobileBandProfileScreen.module.scss";

export interface MobileBandProfileScreenProps {
  /** The band whose profile shows — the friend the caller tapped (from the route param). */
  bandId: string;
  /** History back (supplied by the route wrapper; the screen stays render-context free). */
  onBack: () => void;
  /** Open a clickable band-friend's own profile — a history push to `/contacts/band/{id}`. */
  onOpenFriend: (bandId: string) => void;
  /** Open a member's contact profile — a history push to `/contacts/person/{id}` (ART-194). */
  onOpenMember: (personId: string) => void;
}

/**
 * The MOBILE friend/band profile — the full-screen counterpart of the desktop `BandProfileModal`
 * (ART-195). Reads the same `FriendProfileRead` (header / members / friends / upcoming shows) and reuses the
 * modal's light-body section components; only the FRAME differs (a full-bleed hero + one-column body on the
 * light canvas, versus the modal's dark rail / paper split). The shell header carries the "Friend" title
 * (see `mobileSectionTitle`), so this screen renders none.
 *
 * Render-context free (mount-testable via `renderWithBand`): navigation rides callbacks the route wrapper
 * supplies, never `useNavigate`/`<Link>` here. Back lives in the shell header's leading slot
 * (logo-replacement), a member tap routes to the person screen, and a band-friend tap pushes another
 * profile onto history via `onOpenFriend`.
 */
export function MobileBandProfileScreen({ bandId, onBack, onOpenFriend, onOpenMember }: MobileBandProfileScreenProps) {
  const q = useBandProfileView(bandId);
  const header = q.data?.header;
  const name = header?.bandName || "Band";
  // The hero rides the ONE band-photo path (the tenant-less global avatar route), so a friend band the
  // viewer holds no seat on still gets its picture; photo-less stays plain ink (no initials on the hero).
  const heroUrl = useAvatarUrl("band", bandId, header?.avatarMediaId) ?? null;
  // Back lives in the shell header's leading slot (logo-replacement), not in the hero.
  useMobileBack(onBack);

  if (q.isLoading || q.isError) {
    return (
      <div className={styles.profile} data-testid="mobile-band-profile">
        {q.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={22} label="Loading profile…" />
          </div>
        ) : q.error instanceof ApiError && q.error.status === 404 ? (
          <EmptyState title="Profile not available" description="You're no longer friends, or this band is gone." />
        ) : (
          <EmptyState
            title="Couldn't load this profile"
            description={q.error instanceof Error ? q.error.message : "Please try again."}
            action={<Button onClick={() => void q.refetch()}>Retry</Button>}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.profile} data-testid="mobile-band-profile">
      <div className={heroUrl ? styles.hero : `${styles.hero} ${styles.heroBare}`}>
        {heroUrl && <img className={styles.heroPhoto} src={heroUrl} alt="" data-photo="" />}
        <div className={styles.heroText}>
          <h1 className={styles.heroName}>{name}</h1>
          {header?.location && <p className={styles.location}>{header.location}</p>}
          {(header?.genres?.length ?? 0) > 0 && (
            <p className={styles.genres}>{header?.genres?.join(" · ")}</p>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {/* A member tap opens their contact profile — the mobile person SCREEN (ART-194), a history push,
            not an in-screen modal. Only members carrying a personId are navigable. */}
        <MembersSection
          members={q.data?.members ?? []}
          onOpenMember={(m) => m.personId && onOpenMember(m.personId)}
        />
        <BandFriends friends={q.data?.friends ?? []} onOpenFriend={onOpenFriend} />
        <ShowsSection shows={q.data?.upcomingShows ?? []} />
      </div>
    </div>
  );
}

/**
 * Band Friends on the LIGHT canvas — the modal's `FriendsSection` is styled for its DARK rail (white text),
 * so this is a light-canvas twin: the same clickable/passive rule (a band within reach navigates; the rest
 * render passive), drawn with `BandAvatar` (the one band-photo path) so a friend's picture resolves too.
 */
function BandFriends({
  friends,
  onOpenFriend,
}: {
  friends: FriendProfileFriend[];
  onOpenFriend: (bandId: string) => void;
}) {
  return (
    <section className={styles.section} aria-labelledby="mobile-band-friends-heading">
      <p id="mobile-band-friends-heading" className={styles.sectionLabel}>
        Band friends
      </p>
      {friends.length === 0 ? (
        <p className={styles.sectionEmpty}>No friends listed.</p>
      ) : (
        <ul className={styles.friendGrid}>
          {friends.map((f) => {
            const label = f.name || "Band";
            const avatar = (
              <BandAvatar bandId={f.bandId} avatarMediaId={f.avatarMediaId} initials={bandInitials(f.name)} size="lg" />
            );
            if (f.clickable && f.bandId) {
              return (
                <li key={f.bandId}>
                  <button
                    type="button"
                    className={styles.friendTile}
                    onClick={() => onOpenFriend(f.bandId as string)}
                  >
                    {avatar}
                    <span className={styles.friendName}>{label}</span>
                  </button>
                </li>
              );
            }
            return (
              <li key={f.bandId ?? label}>
                <div className={`${styles.friendTile} ${styles.friendTilePassive}`} aria-disabled="true">
                  {avatar}
                  <span className={styles.friendName}>{label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
